// @tennis-matches/services/governance — Superadmin: Vereine anlegen und listen

import { getSupabaseClient } from "../supabase/index.js";
import { buildClubImportPayload } from "./superadmin-club-import.duplicates.js";
import { normalizeDiscoverWebsiteUrl } from "./superadmin-club-discover.parse.js";
import { CLUB_IMPRINT_PROFILE_KEYS } from "./superadmin-club-imprint.fields.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __isRpcMissing(error) {
  const code = __asStr(error?.code);
  const msg = __asStr(error?.message).toLowerCase();
  const status = Number(error?.status ?? error?.statusCode);
  return (
    code === "PGRST202" ||
    code === "42883" ||
    status === 404 ||
    msg.includes("could not find the function") ||
    msg.includes("schema cache")
  );
}

function __rpcErrorMessage(error) {
  return __asStr(error?.message) || __asStr(error?.details) || "RPC-Fehler";
}

const __PROFILE_KEYS = new Set(CLUB_IMPRINT_PROFILE_KEYS);

function __splitProfile(record) {
  const src = record && typeof record === "object" ? record : {};
  const clubName = __asStr(src.club_name);
  const profile = {};
  for (const [key, value] of Object.entries(src)) {
    if (key === "club_name") continue;
    if (!__PROFILE_KEYS.has(key)) continue;
    const v = __asStr(value);
    if (v) profile[key] = key === "website_url" ? normalizeDiscoverWebsiteUrl(v) : v;
  }
  return {
    clubName,
    city: __asStr(profile.city),
    websiteUrl: __asStr(profile.website_url),
    profile,
  };
}

const __CLUB_PROFILE_LIST_SELECT =
  "tenant_id, club_name, club_short_name, legal_name, city, postal_code, street, house_number, country, website_url, email, phone, register_court, register_number, dtb_club_id, btv_club_id, founded_year, duplicate_marked, duplicate_of_tenant_id, duplicate_marked_at, website_trust_status, website_trust_updated_at, created_at";

/**
 * Fallback: liest bestehende Vereine aus public.club_profiles (3NF, 1:1 pro Tenant).
 * Globaler Superadmin darf alle Profile per RLS lesen; tenants.status ist ohne RPC nicht verfügbar.
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase
 * @param {number} limit
 */
async function __listClubsFromClubProfilesRest(supabase, limit, includeDuplicates = false) {
  let query = supabase
    .from("club_profiles")
    .select(__CLUB_PROFILE_LIST_SELECT)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (!includeDuplicates) {
    query = query.eq("duplicate_marked", false);
  }

  const { data, error } = await query;

  if (error) {
    return { ok: false, reason: "rest-error", error, items: [] };
  }

  const items = (Array.isArray(data) ? data : [])
    .map((row) =>
      __mapClubRow({
        tenant_id: row.tenant_id,
        club_name: __asStr(row.club_name) || "Unbenannter Verein",
        club_short_name: row.club_short_name,
        legal_name: row.legal_name,
        city: row.city,
        postal_code: row.postal_code,
        street: row.street,
        house_number: row.house_number,
        country: row.country,
        website_url: row.website_url,
        email: row.email,
        phone: row.phone,
        register_court: row.register_court,
        register_number: row.register_number,
        dtb_club_id: row.dtb_club_id,
        btv_club_id: row.btv_club_id,
        founded_year: row.founded_year,
        duplicate_marked: row.duplicate_marked,
        duplicate_of_tenant_id: row.duplicate_of_tenant_id,
        duplicate_marked_at: row.duplicate_marked_at,
        website_trust_status: row.website_trust_status,
        website_trust_updated_at: row.website_trust_updated_at,
        status: "active",
        created_at: row.created_at,
      })
    )
    .filter(Boolean);

  return { ok: true, items, source: "club_profiles-rest", degraded: true };
}

function __mapClubRow(row) {
  if (!row || typeof row !== "object") return null;
  return {
    tenantId: __asStr(row.tenant_id),
    clubName: __asStr(row.club_name),
    city: __asStr(row.city),
    postalCode: __asStr(row.postal_code),
    street: __asStr(row.street),
    houseNumber: __asStr(row.house_number),
    websiteUrl: __asStr(row.website_url),
    email: __asStr(row.email),
    phone: __asStr(row.phone),
    shortName: __asStr(row.club_short_name),
    legalName: __asStr(row.legal_name),
    country: __asStr(row.country) || "DE",
    registerCourt: __asStr(row.register_court),
    registerNumber: __asStr(row.register_number),
    dtbClubId: __asStr(row.dtb_club_id),
    btvClubId: __asStr(row.btv_club_id),
    foundedYear: row.founded_year != null ? String(row.founded_year) : "",
    duplicateMarked: row.duplicate_marked === true,
    duplicateOfTenantId: __asStr(row.duplicate_of_tenant_id),
    duplicateMarkedAt: __asStr(row.duplicate_marked_at),
    websiteTrustStatus: __asStr(row.website_trust_status).toLowerCase(),
    websiteTrustUpdatedAt: __asStr(row.website_trust_updated_at),
    status: __asStr(row.status) || "active",
    createdAt: __asStr(row.created_at),
    memberCount: Math.max(0, Number(row.member_count ?? row.memberCount ?? 0) || 0),
  };
}

/**
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase
 * @param {{ clubName: string, city: string, websiteUrl: string, profile: Record<string, string> }} split
 */
async function __rpcCreateClub(supabase, split) {
  const baseArgs = {
    p_club_name: split.clubName,
    p_city: split.city || null,
    p_website_url: split.websiteUrl || null,
  };

  // Migration 363+: immer 4-Parameter (p_profile). Fallback nur für ältere DBs ohne p_profile.
  const withProfile = await supabase.rpc("create_club_as_global_superadmin", {
    ...baseArgs,
    p_profile: split.profile || {},
  });
  if (!withProfile.error || !__isRpcMissing(withProfile.error)) {
    return withProfile;
  }

  return supabase.rpc("create_club_as_global_superadmin", baseArgs);
}

/**
 * @param {{ clubName: string, city?: string, websiteUrl?: string, profile?: object }} params
 */
export async function createClubAsGlobalSuperadminRemote({
  clubName,
  city = "",
  websiteUrl = "",
  profile = {},
} = {}) {
  const split = __splitProfile({ club_name: clubName, city, website_url: websiteUrl, ...profile });
  if (!split.clubName) return { ok: false, reason: "missing-club-name" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await __rpcCreateClub(supabase, split);

  if (error) {
    const msg = __asStr(error.message).toLowerCase();
    if (msg.includes("not-authorized")) return { ok: false, reason: "not-authorized" };
    if (msg.includes("not-authenticated")) return { ok: false, reason: "not-authenticated" };
    if (msg.includes("missing-club-name")) return { ok: false, reason: "missing-club-name" };
    if (__isRpcMissing(error)) {
      return { ok: false, reason: "rpc-missing", error, message: __rpcErrorMessage(error) };
    }
    return { ok: false, reason: "rpc-error", error, message: __rpcErrorMessage(error) };
  }

  const payload = data && typeof data === "object" ? data : {};
  if (payload.ok !== true) {
    return { ok: false, reason: __asStr(payload.reason) || "rpc-failed" };
  }

  return {
    ok: true,
    tenantId: __asStr(payload.tenant_id),
    clubName: __asStr(payload.club_name) || split.clubName,
    city: __asStr(payload.city),
    websiteUrl: __asStr(payload.website_url),
  };
}

async function __enrichClubRpc(supabase, tenantId, profile) {
  return supabase.rpc("enrich_club_as_global_superadmin", {
    p_tenant_id: tenantId,
    p_profile: profile || {},
  });
}

export async function enrichClubAsGlobalSuperadminRemote({ tenantId, profile = {} } = {}) {
  const tid = __asStr(tenantId);
  if (!tid) return { ok: false, reason: "missing-tenant-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const split = __splitProfile(profile);
  const { data, error } = await __enrichClubRpc(supabase, tid, split.profile);

  if (error) {
    const msg = __asStr(error.message).toLowerCase();
    if (msg.includes("not-authorized")) return { ok: false, reason: "not-authorized" };
    if (__isRpcMissing(error)) {
      return {
        ok: false,
        reason: "rpc-missing",
        message: __rpcErrorMessage(error),
      };
    }
    return { ok: false, reason: "rpc-error", message: __rpcErrorMessage(error) };
  }

  const payload = data && typeof data === "object" ? data : {};
  return {
    ok: payload.ok === true,
    tenantId: __asStr(payload.tenant_id) || tid,
    clubName: __asStr(payload.club_name),
  };
}

export async function updateClubAsGlobalSuperadminRemote({ tenantId, profile = {} } = {}) {
  const tid = __asStr(tenantId);
  if (!tid) return { ok: false, reason: "missing-tenant-id" };

  const split = __splitProfile({ club_name: profile?.club_name, ...profile });
  const patch = { ...split.profile };
  if (profile?.club_name != null || split.clubName) {
    patch.club_name = split.clubName || __asStr(profile?.club_name);
  }
  if (profile?.duplicate_marked !== undefined) {
    patch.duplicate_marked =
      profile.duplicate_marked === true || String(profile.duplicate_marked).toLowerCase() === "true";
  }
  if (profile?.duplicate_of_tenant_id !== undefined) {
    patch.duplicate_of_tenant_id = __asStr(profile.duplicate_of_tenant_id) || null;
  }
  if (profile?.website_trust_status !== undefined) {
    const trust = __asStr(profile.website_trust_status).toLowerCase();
    patch.website_trust_status = trust === "ok" || trust === "suspicious" ? trust : null;
  }

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc("update_club_as_global_superadmin", {
    p_tenant_id: tid,
    p_profile: patch,
  });

  if (error) {
    const msg = __asStr(error.message).toLowerCase();
    if (msg.includes("not-authorized")) return { ok: false, reason: "not-authorized" };
    if (__isRpcMissing(error)) {
      return { ok: false, reason: "rpc-missing", message: __rpcErrorMessage(error) };
    }
    return { ok: false, reason: "rpc-error", message: __rpcErrorMessage(error) };
  }

  const payload = data && typeof data === "object" ? data : {};
  return {
    ok: payload.ok === true,
    tenantId: __asStr(payload.tenant_id) || tid,
    clubName: __asStr(payload.club_name),
  };
}

export async function deleteClubAsGlobalSuperadminRemote({ tenantId } = {}) {
  const tid = __asStr(tenantId);
  if (!tid) return { ok: false, reason: "missing-tenant-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc("delete_club_as_global_superadmin", {
    p_tenant_id: tid,
  });

  if (error) {
    const msg = __asStr(error.message).toLowerCase();
    if (msg.includes("not-authorized")) return { ok: false, reason: "not-authorized" };
    if (msg.includes("not-authenticated")) return { ok: false, reason: "not-authenticated" };
    if (msg.includes("missing-tenant-id")) return { ok: false, reason: "missing-tenant-id" };
    if (msg.includes("tenant-not-found")) return { ok: false, reason: "tenant-not-found" };
    if (msg.includes("protected-tenant")) return { ok: false, reason: "protected-tenant" };
    if (msg.includes("tenant-has-members")) return { ok: false, reason: "tenant-has-members" };
    if (msg.includes("tenant-has-bindings")) return { ok: false, reason: "tenant-has-bindings" };
    if (msg.includes("tenant-has-data")) return { ok: false, reason: "tenant-has-data" };
    if (__isRpcMissing(error)) {
      return { ok: false, reason: "rpc-missing", message: __rpcErrorMessage(error) };
    }
    return { ok: false, reason: "rpc-error", message: __rpcErrorMessage(error) };
  }

  const payload = data && typeof data === "object" ? data : {};
  if (payload.ok !== true) {
    return { ok: false, reason: __asStr(payload.reason) || "rpc-failed" };
  }

  return {
    ok: true,
    tenantId: __asStr(payload.tenant_id) || tid,
    clubName: __asStr(payload.club_name),
  };
}

async function __bulkCreateClubsSequentialFromPayload(list) {
  const supabase = await getSupabaseClient();
  let created = 0;
  let enriched = 0;
  let skipped = 0;
  let failed = 0;
  const errors = [];
  const skippedItems = [];

  for (let i = 0; i < list.length; i++) {
    const row = list[i];
    const action = __asStr(row?.import_action) || "create";
    const split = __splitProfile(row);
    if (!split.clubName) {
      skipped += 1;
      continue;
    }

    if (action === "skip") {
      skipped += 1;
      skippedItems.push({
        row: i + 1,
        club_name: split.clubName,
        city: split.city,
        reason: __asStr(row?.skip_reason) || "manual",
        import_action: "skip",
        row_data: split.profile,
      });
      continue;
    }

    if (action === "enrich") {
      const tenantId = __asStr(row?.match_tenant_id);
      if (!tenantId) {
        failed += 1;
        errors.push({ row: i + 1, club_name: split.clubName, message: "enrich-target-not-found" });
        continue;
      }
      const res = await enrichClubAsGlobalSuperadminRemote({ tenantId, profile: { club_name: split.clubName, ...split.profile } });
      if (res?.ok) {
        enriched += 1;
      } else {
        failed += 1;
        errors.push({ row: i + 1, club_name: split.clubName, message: res?.message || res?.reason || "enrich-failed" });
      }
      continue;
    }

    const res = await createClubAsGlobalSuperadminRemote({
      clubName: split.clubName,
      city: split.city,
      websiteUrl: split.websiteUrl,
      profile: split.profile,
    });

    if (res?.ok) {
      created += 1;
      continue;
    }

    failed += 1;
    errors.push({
      row: i + 1,
      club_name: split.clubName,
      message: res?.message || res?.reason || "create-failed",
    });
  }

  return {
    ok: failed === 0 || created > 0 || enriched > 0,
    created,
    enriched,
    skipped,
    failed,
    errors,
    skippedItems,
    mode: "sequential",
  };
}

/**
 * @param {{
 *   rows?: Array<Record<string, string>>,
 *   analyzedRows?: Array<{ row: Record<string, string>, action?: string, matchTenantId?: string, status?: string }>,
 *   skipDuplicates?: boolean,
 * }} params
 */
export async function bulkCreateClubsAsGlobalSuperadminRemote({ rows, analyzedRows, skipDuplicates = false } = {}) {
  const list = Array.isArray(analyzedRows)
    ? buildClubImportPayload(analyzedRows)
    : Array.isArray(rows)
      ? rows
          .map((row) => {
            const split = __splitProfile(row);
            if (!split.clubName) return null;
            return { club_name: split.clubName, ...split.profile, import_action: "create" };
          })
          .filter(Boolean)
      : [];

  if (list.length === 0) {
    return {
      ok: false,
      reason: "empty-rows",
      created: 0,
      enriched: 0,
      skipped: 0,
      failed: 0,
      errors: [],
      skippedItems: [],
    };
  }

  const supabase = await getSupabaseClient();
  if (!supabase) {
    return {
      ok: false,
      reason: "no-supabase",
      created: 0,
      enriched: 0,
      skipped: 0,
      failed: 0,
      errors: [],
      skippedItems: [],
    };
  }

  const { data, error } = await supabase.rpc("bulk_create_clubs_as_global_superadmin", {
    p_rows: list,
    p_skip_duplicates: skipDuplicates === true,
  });

  if (error) {
    const msg = __asStr(error.message).toLowerCase();
    if (msg.includes("not-authorized")) {
      return {
        ok: false,
        reason: "not-authorized",
        created: 0,
        enriched: 0,
        skipped: 0,
        failed: 0,
        errors: [],
        skippedItems: [],
      };
    }
    if (__isRpcMissing(error)) {
      return __bulkCreateClubsSequentialFromPayload(list);
    }
    return {
      ok: false,
      reason: "rpc-error",
      error,
      message: __rpcErrorMessage(error),
      created: 0,
      enriched: 0,
      skipped: 0,
      failed: 0,
      errors: [],
      skippedItems: [],
    };
  }

  const payload = data && typeof data === "object" ? data : {};
  return {
    ok: payload.ok !== false,
    created: Number(payload.created) || 0,
    enriched: Number(payload.enriched) || 0,
    skipped: Number(payload.skipped) || 0,
    failed: Number(payload.failed) || 0,
    errors: Array.isArray(payload.errors) ? payload.errors : [],
    skippedItems: Array.isArray(payload.skipped_items) ? payload.skipped_items : [],
    mode: "bulk",
  };
}

/**
 * @param {{ limit?: number, includeDuplicates?: boolean }} [opts]
 */
export async function listClubsForGlobalSuperadminRemote(opts = {}) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", items: [] };

  const limit = Number(opts.limit) > 0 ? Math.min(Number(opts.limit), 500) : 100;
  const includeDuplicates = opts.includeDuplicates === true;
  const { data, error } = await supabase.rpc("list_clubs_for_global_superadmin", {
    p_limit: limit,
    p_include_duplicates: includeDuplicates,
  });

  if (error) {
    const msg = __asStr(error.message).toLowerCase();
    if (__isRpcMissing(error) || msg.includes("404")) {
      const fallback = await __listClubsFromClubProfilesRest(supabase, limit, includeDuplicates);
      if (fallback.ok) return fallback;
      return {
        ok: false,
        reason: "rpc-missing",
        error,
        items: [],
        message: __rpcErrorMessage(error),
      };
    }
    if (msg.includes("not-authorized")) return { ok: false, reason: "not-authorized", items: [] };
    return { ok: false, reason: "rpc-error", error, items: [] };
  }

  const payload = data && typeof data === "object" ? data : {};
  const items = Array.isArray(payload.items)
    ? payload.items.map(__mapClubRow).filter(Boolean)
    : [];

  return { ok: payload.ok !== false, items, source: "rpc-list_clubs_for_global_superadmin" };
}

export { CLUB_IMPORT_FIELDS, suggestClubImportMapping, mapClubImportRows } from "./superadmin-club-import.fields.js";
export { CLUB_IMPRINT_PROFILE_KEYS, CLUB_ADD_FORM_FIELDS } from "./superadmin-club-imprint.fields.js";
export { parseClubImportSpreadsheet } from "./superadmin-club-import.parse.js";
export {
  analyzeClubImportDuplicates,
  compareClubImportWithExisting,
  clubDuplicateKey,
  buildClubImportPayload,
} from "./superadmin-club-import.duplicates.js";
export {
  SUPERADMIN_CLUB_LIST_COLUMN_DEFS,
  SUPERADMIN_CLUB_LIST_COLUMNS_STORAGE_KEY,
  SUPERADMIN_CLUB_LIST_COLUMN_STICKY_WIDTHS,
  getDefaultSuperadminClubListVisibleColumnIds,
  getDefaultSuperadminClubListPinnedColumnIds,
  loadSuperadminClubListVisibleColumnIds,
  loadSuperadminClubListPinnedColumnIds,
  loadSuperadminClubListColumnLayout,
  saveSuperadminClubListVisibleColumnIds,
  saveSuperadminClubListColumnLayout,
  normalizeSuperadminClubListPinnedColumnIds,
  resolveSuperadminClubListVisibleColumns,
  computeSuperadminClubListStickyLeftOffsets,
  getSuperadminClubListColumnStickyWidth,
  formatSuperadminClubListCellValue,
  isSuperadminClubListCellEmpty,
  getSuperadminClubListColumnDef,
} from "./superadmin-club-list.columns.js";
export {
  SUPERADMIN_CLUB_LIST_INLINE_EDIT_STORAGE_KEY,
  loadSuperadminClubListInlineEditEnabled,
  saveSuperadminClubListInlineEditEnabled,
  isSuperadminClubListColumnInlineEditable,
  getSuperadminClubListInlineEditValue,
  buildSuperadminClubListInlineUpdatePayload,
  superadminClubListInlineCellKey,
} from "./superadmin-club-list.inline.js";
